import Link from "next/link";
import { useRouter } from "next/router";

import { useAuth } from "@/auth/hooks";
import { useGuestModalGuard } from "@/common/hooks/guest.hooks";
import { useOpenHomeCopy } from "@/features/open-home/client";
import { OPEN_HOME_LOCALES } from "@/features/open-home/copy";
import {
  OPEN_HOME_PROMO_GATE,
  OPEN_HOME_PUBLIC_GATE,
  useOpenHomeGate,
} from "@/features/open-home/gate";
import { NewLabel } from "@/ui/cva/NewLabel";

/** Dots on the promo photo, in % of the image: the Vertigo, the Togo and the coffee table. */
const DOTS = [
  { left: 51, top: 13 },
  { left: 69, top: 66 },
  { left: 55, top: 85 },
];

/**
 * Home page block promoting Open Home (design 2026-10-03), right under the hero. Shows a room with
 * the dots buyers tap, so the idea lands before anyone reads. One strong action; the second one is
 * for people with pieces at home; no room type chips (Thomas, 10-10: no filtering yet). On
 * flavour-100: brand green is not a light background. Logged-out visitors
 * get the sign-up prompt and land in Open Home afterwards, which is the sign-up accelerator.
 */
/** Rendered only when the `open_home_home_promo` and `open_home_public` Statsig gates are on. */
export const OpenHomePromoBlock = () => {
  const promo = useOpenHomeGate(OPEN_HOME_PROMO_GATE);
  const isPublic = useOpenHomeGate(OPEN_HOME_PUBLIC_GATE);

  return promo && isPublic ? <OpenHomePromoBlockContent /> : null;
};

const OpenHomePromoBlockContent = () => {
  const copy = useOpenHomeCopy();
  const router = useRouter();
  const { isLoggedIn } = useAuth();
  const { guestGuard } = useGuestModalGuard();

  if (!OPEN_HOME_LOCALES.includes(router.locale as "en")) {
    return null;
  }

  const go = (href: string) => async (event: React.MouseEvent) => {
    if (isLoggedIn) {
      return;
    }

    event.preventDefault();

    const signedIn = await guestGuard({
      title: copy.loginTitle,
      description: copy.loginBody,
      referrer: "open-home-homepage-block",
    });

    if (signedIn) {
      router.push(href);
    }
  };

  return (
    <section
      aria-labelledby="open-home-promo-title"
      className="grid overflow-hidden rounded-2xl bg-flavour-100 md:grid-cols-[1.1fr_1fr] md:rounded-3xl"
    >
      <Link
        href="/open-home"
        onClick={go("/open-home")}
        className="relative block h-56 sm:h-72 md:h-auto md:min-h-[24rem]"
        tabIndex={-1}
        aria-hidden
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/images/open-home/promo-room.jpg"
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
        />
        {DOTS.map(dot => (
          <span
            key={`${dot.left}-${dot.top}`}
            className="absolute flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center"
            style={{ left: `${dot.left}%`, top: `${dot.top}%` }}
          >
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-60" />
            <span className="relative h-5 w-5 rounded-full border-4 border-white bg-brand-500 shadow-lg" />
          </span>
        ))}
        <span className="absolute bottom-3 left-3 flex items-center gap-2.5 rounded-xl bg-white py-1.5 pl-1.5 pr-3 shadow-xl md:bottom-6 md:left-6">
          <span
            className="h-10 w-10 shrink-0 rounded-lg bg-cover"
            style={{
              backgroundImage: "url(/images/open-home/promo-room.jpg)",
              backgroundSize: "560%",
              backgroundPosition: "78% 72%",
            }}
          />
          <span className="text-left">
            <span className="block text-xs font-bold text-[#1C1818]">
              {copy.homeSampleTitle}
            </span>
            <span className="block text-xs font-bold text-attention-500">
              {copy.noPrice}
            </span>
          </span>
        </span>
      </Link>

      <div className="flex flex-col gap-3 p-5 md:justify-center md:gap-4 md:p-10">
        <p className="flex items-center gap-2 font-title text-sm font-bold text-brand-600">
          <NewLabel>{copy.homeNew}</NewLabel>
          {copy.name}
        </p>
        <h2
          id="open-home-promo-title"
          className="font-title text-2xl font-bold leading-tight text-brand-700 md:text-4xl"
        >
          {copy.homeTitle}
        </h2>
        <p className="text-sm text-[#464646] md:hidden">{copy.homeBodyShort}</p>
        <p className="hidden text-base text-[#464646] md:block">
          {copy.homeBody}
        </p>
        <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
          <Link
            href="/open-home"
            onClick={go("/open-home")}
            className="flex h-12 items-center justify-center rounded-xl bg-brand-500 px-6 font-title font-bold text-white hover:bg-brand-600"
          >
            {copy.homeCta} →
          </Link>
          <Link
            href="/open-home/my-home"
            onClick={go("/open-home/my-home")}
            className="text-center text-sm font-bold text-brand-600 underline underline-offset-4"
          >
            {copy.openYourHome}
          </Link>
        </div>
        <ul className="mt-1 hidden flex-wrap gap-x-5 gap-y-1 text-xs text-[#464646] md:flex">
          {copy.homeTrust.map(line => (
            <li key={line}>
              <span className="font-bold text-brand-500">✓</span> {line}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};
